/**
 * Users API Service
 * Handles all API calls for the users management module
 *
 * REFACTORIZADO: Ahora usa el cliente API centralizado
 * Ver: @/services/apiClient.js y @/config/apiEndpoints.js
 */
import { apiClient } from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../config/apiEndpoints';

const UsersAPI = {
  /**
   * List users with filters and pagination
   * @param {Object} params - Query parameters (page, per_page, search, role, status, etc.)
   * @returns {Promise<Object>} - { items, total, total_pages, page, per_page }
   */
  listUsers: async (params = {}) => {
    const response = await apiClient.get(API_ENDPOINTS.USERS_LIST, {}, params);
    return response.data;
  },

  /**
   * Get user detail by ID
   * @param {number} userId - User ID
   * @returns {Promise<Object>} - User detail with account, identity, profiles, etc.
   */
  getUserDetail: async (userId) => {
    const response = await apiClient.get(API_ENDPOINTS.USERS_DETAIL, { user_id: userId });
    return response.data;
  },

  /**
   * Update account status (activate, deactivate, lock, unlock)
   * @param {number} userId - User ID
   * @param {string} action - Action to perform
   * @param {string} reason - Reason for the action
   * @param {number} lockDurationHours - Duration for lock (optional)
   * @returns {Promise<Object>} - Updated account status
   */
  updateAccountStatus: async (userId, action, reason, lockDurationHours = null) => {
    const data = { action, reason };
    if (lockDurationHours) {
      data.lock_duration_hours = lockDurationHours;
    }
    const response = await apiClient.put(API_ENDPOINTS.USERS_STATUS, { user_id: userId }, data);
    return response.data;
  },

  /**
   * Assign role to user
   * @param {number} userId - User ID
   * @param {number} roleId - Role ID to assign
   * @param {Array} customPermissions - Custom permissions (optional)
   * @returns {Promise<Object>} - Created profile
   */
  assignRole: async (userId, roleId, customPermissions = null) => {
    const data = { role_id: roleId };
    if (customPermissions) {
      data.custom_permissions = customPermissions;
    }
    const response = await apiClient.post(API_ENDPOINTS.USERS_PROFILES, { user_id: userId }, data);
    return response.data;
  },

  /**
   * Remove profile/role from user
   * @param {number} userId - User ID
   * @param {number} profileId - Profile ID to remove
   * @returns {Promise<Object>} - Result
   */
  removeProfile: async (userId, profileId) => {
    const response = await apiClient.delete(API_ENDPOINTS.USERS_PROFILES_DELETE, {
      user_id: userId,
      profile_id: profileId
    });
    return response.data;
  },

  /**
   * Get user activity log
   * @param {number} userId - User ID
   * @returns {Promise<Array>} - Activity log entries
   */
  getUserActivity: async (userId) => {
    const response = await apiClient.get(API_ENDPOINTS.USERS_ACTIVITY, { user_id: userId });
    return response.data;
  },

  /**
   * Get duplicate accounts matching this user's identity hash
   */
  getUserDuplicates: async (userId) => {
    const response = await apiClient.get(API_ENDPOINTS.USERS_DUPLICATES, { user_id: userId });
    return response.data;
  },

  /**
   * Dismiss duplicate flag (mark as not a duplicate)
   */
  dismissDuplicate: async (userId) => {
    const response = await apiClient.post(API_ENDPOINTS.USERS_DUPLICATES_DISMISS, { user_id: userId });
    return response.data;
  },

  /**
   * Merge source account into target (user_id)
   */
  mergeAccounts: async (userId, sourceAccountId) => {
    const response = await apiClient.post(
      API_ENDPOINTS.USERS_DUPLICATES_MERGE,
      { user_id: userId },
      { source_account_id: sourceAccountId }
    );
    return response.data;
  },

  /**
   * Validate user identity
   * @param {number} userId - User ID
   * @param {string} validationStatus - Validation status
   * @param {string} notes - Notes (optional)
   * @returns {Promise<Object>} - Updated identity
   */
  validateIdentity: async (userId, validationStatus, notes = null) => {
    const data = { validation_status: validationStatus };
    if (notes) {
      data.notes = notes;
    }
    const response = await apiClient.put(API_ENDPOINTS.USERS_VALIDATE_IDENTITY, { user_id: userId }, data);
    return response.data;
  },

  /**
   * Get available roles
   * @returns {Promise<Array>} - List of available roles
   */
  getAvailableRoles: async () => {
    const response = await apiClient.get(API_ENDPOINTS.ADMIN_ROLES_LIST);
    return response.data;
  },

  /**
   * Get all available permissions in the system
   * @returns {Promise<Array>} - List of all permissions
   */
  getAllPermissions: async () => {
    const response = await apiClient.get(API_ENDPOINTS.PERMISSIONS_LIST);
    return response.data;
  },

  /**
   * Get permissions granted to a specific user
   * @param {number} userId - User ID
   * @returns {Promise<Array>} - List of user's permissions
   */
  getUserPermissions: async (userId) => {
    const response = await apiClient.get(API_ENDPOINTS.USERS_PERMISSIONS, { user_id: userId });
    return response.data;
  },

  /**
   * Grant a permission to a user
   * @param {number} userId - User ID
   * @param {number} permissionId - Permission ID to grant
   * @returns {Promise<Object>} - Result
   */
  grantPermission: async (userId, permissionId) => {
    const response = await apiClient.post(
      API_ENDPOINTS.USERS_PERMISSIONS,
      { user_id: userId },
      { permission_id: permissionId }
    );
    return response.data;
  },

  /**
   * Discharge (dar de baja) a user - soft deactivation
   * @param {number} userId - User ID
   * @param {string} reason - Reason for discharge
   * @returns {Promise<Object>} - Result
   */
  dischargeUser: async (userId, reason) => {
    const response = await apiClient.put(API_ENDPOINTS.USERS_STATUS, { user_id: userId }, {
      action: 'discharge',
      reason
    });
    return response.data;
  },

  /**
   * Permanently delete a user
   * @param {number} userId - User ID
   * @returns {Promise<Object>} - Result
   */
  deleteUser: async (userId) => {
    const response = await apiClient.delete(API_ENDPOINTS.USERS_DETAIL, { user_id: userId });
    return response.data;
  },

  /**
   * Revoke a permission from a user
   * @param {number} userId - User ID
   * @param {number} permissionId - Permission ID to revoke
   * @returns {Promise<Object>} - Result
   */
  revokePermission: async (userId, permissionId) => {
    const response = await apiClient.delete(
      API_ENDPOINTS.USERS_PERMISSIONS_DELETE,
      { user_id: userId, permission_id: permissionId }
    );
    return response.data;
  }
};

export default UsersAPI;
